import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// MITRA ONE CARE PLATFORM ADMINISTRATION — /api/moc-admin/*, super-admin only.
//
//   /moc-admin/patients                       the GLOBAL identity layer: one
//                                             MOC account per person, reaching
//                                             every connected hospital
//   /moc-admin/patients/:id/activity          cross-hospital timeline + counts
//   /moc-admin/patients/:id/clinical          break-glass, behind a logged reason
//   /moc-admin/services                       patient-app service visibility
//
// This lived in the Tenant #1 admin app, where it had to be fenced off with an
// "is this the open-platform tenant?" guard. In the Super Admin portal these are
// plain super-admin routes — the API gate (requireSuperAdmin) is the whole story
// and the client-side open-platform guard has no job left to do.

const reject = (error, fallback) => ({
  error: error.response?.data?.message || error.message || fallback,
  // The documented failure codes (reason_too_short, not_this_identity, …). The
  // break-glass modal words them itself; `error` is only the fallback text.
  code: error.response?.data?.code || null,
});

// EVERY filter axis is a SERVER parameter, on purpose.
//
//   search    name / surname / email / phone / MOC id
//   status    active | inactive
//   verified  email | phone | both | any | none   (emailVerified/phoneVerified)
//   gender    comma-separated; matches the newest linked hospital row's gender
//   tenantId  comma-separated hospital ids; identities with a LIVE link to any
//   minLinks  inclusive lower bound on LIVE moc_account_patients rows
//   maxLinks  inclusive upper bound  (so 0/0 = "not linked to any hospital yet")
//
// verified and the two link bounds used to be applied in the browser over the
// rows already fetched, which quietly turned `total` into a lie: the pager
// counted the unfiltered set and every page but the current one was unfiltered.
// They are real predicates on the API's own query now — filtering and counting
// happen together or not at all, so never reintroduce a client-side pass here.
export const fetchMocPatients = createAsyncThunk(
  'moc/fetchMocPatients',
  async ({ search = '', status = '', verified = '', gender = '', tenantId = '', minLinks, maxLinks, page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.get(`${baseURL}/moc-admin/patients`, {
        params: {
          page,
          limit,
          search: search || undefined,
          status: status || undefined,
          verified: verified || undefined,
          gender: gender || undefined,
          tenantId: tenantId || undefined,
          // 0 is a MEANINGFUL bound ("no hospital links at all"), so these test
          // for null/undefined rather than falsiness — `maxLinks || undefined`
          // would drop exactly the filter the "Not linked yet" option needs.
          minLinks: minLinks == null ? undefined : minLinks,
          maxLinks: maxLinks == null ? undefined : maxLinks,
        },
      });
      return data;
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to load MOC patients'));
    }
  }
);

export const fetchMocPatient = createAsyncThunk(
  'moc/fetchMocPatient',
  async (mocAccountId, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.get(`${baseURL}/moc-admin/patients/${mocAccountId}`);
      return data;
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to load this MOC patient'));
    }
  }
);

export const setMocPatientStatus = createAsyncThunk(
  'moc/setMocPatientStatus',
  async ({ mocAccountId, status }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      await axiosInstance.patch(`${baseURL}/moc-admin/patients/${mocAccountId}/status`, { status });
      // The API answers with the updated row, but the only field that can have
      // changed is the one we just sent — echo it back so list and detail can be
      // patched without a refetch.
      return { mocAccountId, status };
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to update the account status'));
    }
  }
);

// SUPER ADMIN CREATES MOC IDENTITIES (owner, 2026-09-02) — single create and
// the platform's ONLY bulk-creation path (the tenant app's bulk upload was
// deleted the same day). Both make GLOBAL MOC accounts, never tenant rows;
// at least one contact (e-mail or mobile), none marked verified — the server
// sends the login id(s) and a temporary password, and the patient verifies
// their contacts and chooses a password at their first sign-in (owner,
// 2 Oct 2026). A create with no contact answers 400 IDENTIFIER_REQUIRED.
export const createMocPatient = createAsyncThunk(
  'moc/createMocPatient',
  async (payload, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.post(`${baseURL}/moc-admin/patients`, payload);
      return data;
    } catch (error) {
      return rejectWithValue({
        ...reject(error, 'Failed to create the MOC patient'),
        // FIELD_REQUIRED / ACCOUNT_EXISTS answers name the form control.
        field: error.response?.data?.field || null,
      });
    }
  }
);

export const downloadMocPatientSample = createAsyncThunk(
  'moc/downloadMocPatientSample',
  async (_, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.get(`${baseURL}/moc-admin/patients/bulk-sample`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sample_moc_patient_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to download the sample'));
    }
  }
);

export const bulkUploadMocPatients = createAsyncThunk(
  'moc/bulkUploadMocPatients',
  async (file, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axiosInstance.post(`${baseURL}/moc-admin/patients/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to bulk upload MOC patients'));
    }
  }
);

export const fetchMocActivity = createAsyncThunk(
  'moc/fetchMocActivity',
  async ({ mocAccountId, kind = '', limit = 20, offset = 0, tenantId = '' }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.get(`${baseURL}/moc-admin/patients/${mocAccountId}/activity`, {
        // tenantId narrows the whole panel (doctors, counts, timeline) to the
        // selected linked hospital — server-side, so paging stays honest.
        params: { kind: kind || undefined, tenantId: tenantId || undefined, limit, offset },
      });
      return data;
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to load this identity’s activity'));
    }
  }
);

// THE PATIENT HEALTH RECORD (doc/one-patient-row.md §8, Phase 3).
//
// NOT a /moc-admin route, and deliberately so. `health_entries` carries no
// `tenantId` — the record belongs to the PERSON — so utils/healthAccess.js is
// the entire access rule, and it admits super_admin by name beside the
// patient, their family and a doctor who has treated them. One endpoint, one
// payload, whoever is reading: a platform-only variant of this read would be a
// second copy of the rule guarding somebody's allergy list.
//
// There is no tenant parameter to pass and no hospital to pick. Every entry
// carries `recordedAtTenantName`, which is the hospital that actually recorded
// it — the honest answer to "where did this come from".
//
// The API writes a `health_record_read` audit row for a staff read, so simply
// opening the tab is already on the record; nothing extra is sent from here.
export const getHealthRecord = createAsyncThunk(
  'moc/getHealthRecord',
  async (patientId, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.get(`${baseURL}/health-record/${patientId}`);
      return data;
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to load this patient’s health record'));
    }
  }
);

// THE PERSON'S CHECK READINGS (doc/check-sets-plan.md §6, Round A).
//
// A section of the health record with its own endpoint, not an eighth
// category: readings are grouped per VISIT (date, hospital, doctor) and every
// value carries the label and unit of the set AS IT WAS when the reading was
// taken, so the platform never needs a hospital's set definitions to read
// them. Same gate as the record (healthAccess.viewerMayReadRecord admits
// super_admin read-only), NOT feature-gated, and the read is audited
// server-side like the record's own.
//
// Fetched beside the record rather than inside it: a readings failure (an
// environment whose check-sets migration has not run yet) must not take the
// seven categories down with it, and the reverse.
export const getHealthReadings = createAsyncThunk(
  'moc/getHealthReadings',
  async (patientId, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.get(`${baseURL}/health-record/${patientId}/readings`);
      return data;
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to load this patient’s readings'));
    }
  }
);

// THE PERSON'S CARE PROGRAMMES (doc/care-programmes-plan.md, Round B).
//
// Follow-up timelines a doctor started for this person at a hospital — each
// one carries the programme AS IT WAS STARTED (name, start-date label,
// milestones) and a timeline the server computes on every read (week, due
// dates, done / due / late / upcoming, and what proved a milestone done).
// Nothing here knows what a programme "should" contain: every programme is
// defined by a doctor or a hospital, for any speciality.
//
// Same gate as the record (healthAccess.viewerMayReadRecord admits
// super_admin read-only), NOT feature-gated — a hospital that switched the
// feature off must not hide a programme from the person's record — and
// audited server-side like the record's own read. Its own request beside the
// record and the readings, so one failing never blanks the others.
export const getHealthProgrammes = createAsyncThunk(
  'moc/getHealthProgrammes',
  async (patientId, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.get(`${baseURL}/health-record/${patientId}/programmes`);
      return data;
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to load this patient’s programmes'));
    }
  }
);

// BREAK-GLASS. Every call writes an audit row BEFORE any clinical field is read
// — including the refusals — so this is never a silent retry loop: each press of
// the button is one more entry with the operator's name on it.
export const openClinicalDetail = createAsyncThunk(
  'moc/openClinicalDetail',
  async ({ mocAccountId, reason, kind, ref }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.post(
        `${baseURL}/moc-admin/patients/${mocAccountId}/clinical`,
        { reason, kind, ref }
      );
      return data;
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to open the clinical detail'));
    }
  }
);

export const fetchServiceVisibility = createAsyncThunk(
  'moc/fetchServiceVisibility',
  async (_, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.get(`${baseURL}/moc-admin/services`);
      return data?.services || [];
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to load service visibility'));
    }
  }
);

// Only the CHANGED rows are sent. The API accepts a partial list and validates
// all-or-nothing, so a rejected save leaves the platform exactly as it was and
// the screen keeps showing the operator's unsaved draft.
export const saveServiceVisibility = createAsyncThunk(
  'moc/saveServiceVisibility',
  async (services, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.put(`${baseURL}/moc-admin/services`, { services });
      return data?.services || [];
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to save service visibility'));
    }
  }
);

const initialState = {
  // List
  patients: [],
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 0,
  listLoading: false,
  listError: null,

  // One identity
  detail: null,
  detailLoading: false,
  detailError: null,
  statusUpdating: false,

  // Cross-hospital activity for the open identity
  activity: null,
  activityLoading: false,
  activityError: null,

  // The person's health record — seven categories, read-only
  healthRecord: null,
  healthRecordLoading: false,
  healthRecordError: null,

  // The person's check readings, per visit — read-only. `healthReadingsFor`
  // is the patientId of the request in flight: an answer for a person the
  // page has already left is dropped instead of painted under the new name.
  healthReadings: null,
  healthReadingsFor: null,
  healthReadingsLoading: false,
  healthReadingsError: null,

  // The person's care programmes (active + closed, each with its computed
  // timeline) — read-only. `healthProgrammesFor` guards against a late
  // answer for a person the page has already left, as for the readings.
  healthProgrammes: null,
  healthProgrammesFor: null,
  healthProgrammesLoading: false,
  healthProgrammesError: null,

  // Break-glass result for ONE timeline row
  clinical: null,
  clinicalLoading: false,
  clinicalError: null,
  clinicalErrorCode: null,

  // Patient-app service visibility
  services: [],
  servicesLoading: false,
  servicesError: null,
  servicesSaving: false,
};

const mocSlice = createSlice({
  name: 'moc',
  initialState,
  reducers: {
    // Called when the detail page mounts or its :mocAccountId changes. Without
    // it the previous identity's records paint for a frame under the new name.
    resetMocPatientDetail: (state) => {
      state.detail = null;
      state.detailError = null;
      state.activity = null;
      state.activityError = null;
      // The record is the heaviest thing on the page and the one it would be
      // worst to show under the wrong name — cleared with the rest.
      state.healthRecord = null;
      state.healthRecordError = null;
      state.healthReadings = null;
      state.healthReadingsFor = null;
      state.healthReadingsLoading = false;
      state.healthReadingsError = null;
      state.healthProgrammes = null;
      state.healthProgrammesFor = null;
      state.healthProgrammesLoading = false;
      state.healthProgrammesError = null;
    },
    // Break-glass results are never carried between modals: a record on screen
    // must always have a fresh audit row behind it, so this runs on both open
    // and close rather than only on close.
    clearClinicalDetail: (state) => {
      state.clinical = null;
      state.clinicalError = null;
      state.clinicalErrorCode = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMocPatients.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchMocPatients.fulfilled, (state, action) => {
        state.listLoading = false;
        state.patients = action.payload?.patients || [];
        state.total = action.payload?.total || 0;
        state.page = action.payload?.page || 1;
        state.limit = action.payload?.limit || state.limit;
        state.totalPages = action.payload?.totalPages || 0;
      })
      .addCase(fetchMocPatients.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload?.error || 'Failed to load MOC patients';
      })

      .addCase(fetchMocPatient.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchMocPatient.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload || null;
      })
      .addCase(fetchMocPatient.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload?.error || 'Failed to load this MOC patient';
      })

      .addCase(setMocPatientStatus.pending, (state) => {
        state.statusUpdating = true;
      })
      .addCase(setMocPatientStatus.fulfilled, (state, action) => {
        state.statusUpdating = false;
        const { mocAccountId, status } = action.payload || {};
        if (state.detail?.mocAccountId === mocAccountId) state.detail.status = status;
        const row = state.patients.find((p) => p.mocAccountId === mocAccountId);
        if (row) row.status = status;
      })
      .addCase(setMocPatientStatus.rejected, (state) => {
        state.statusUpdating = false;
      })

      .addCase(fetchMocActivity.pending, (state) => {
        state.activityLoading = true;
        state.activityError = null;
      })
      .addCase(fetchMocActivity.fulfilled, (state, action) => {
        state.activityLoading = false;
        state.activity = action.payload || null;
      })
      .addCase(fetchMocActivity.rejected, (state, action) => {
        state.activityLoading = false;
        state.activityError = action.payload?.error || 'Failed to load this identity’s activity';
      })

      .addCase(getHealthRecord.pending, (state) => {
        state.healthRecordLoading = true;
        state.healthRecordError = null;
      })
      .addCase(getHealthRecord.fulfilled, (state, action) => {
        state.healthRecordLoading = false;
        state.healthRecord = action.payload || null;
      })
      .addCase(getHealthRecord.rejected, (state, action) => {
        state.healthRecordLoading = false;
        state.healthRecordError = action.payload?.error || 'Failed to load this patient’s health record';
      })

      .addCase(getHealthReadings.pending, (state, action) => {
        // A different person: the previous answer is not theirs to show.
        if (state.healthReadingsFor !== action.meta.arg) state.healthReadings = null;
        state.healthReadingsFor = action.meta.arg;
        state.healthReadingsLoading = true;
        state.healthReadingsError = null;
      })
      .addCase(getHealthReadings.fulfilled, (state, action) => {
        if (action.meta.arg !== state.healthReadingsFor) return;
        state.healthReadingsLoading = false;
        state.healthReadings = action.payload || null;
      })
      .addCase(getHealthReadings.rejected, (state, action) => {
        if (action.meta.arg !== state.healthReadingsFor) return;
        state.healthReadingsLoading = false;
        // May be the API's { en, fr } pair; the component picks the language.
        state.healthReadingsError = action.payload?.error || 'Failed to load this patient’s readings';
      })

      .addCase(getHealthProgrammes.pending, (state, action) => {
        // A different person: the previous answer is not theirs to show.
        if (state.healthProgrammesFor !== action.meta.arg) state.healthProgrammes = null;
        state.healthProgrammesFor = action.meta.arg;
        state.healthProgrammesLoading = true;
        state.healthProgrammesError = null;
      })
      .addCase(getHealthProgrammes.fulfilled, (state, action) => {
        if (action.meta.arg !== state.healthProgrammesFor) return;
        state.healthProgrammesLoading = false;
        state.healthProgrammes = action.payload || null;
      })
      .addCase(getHealthProgrammes.rejected, (state, action) => {
        if (action.meta.arg !== state.healthProgrammesFor) return;
        state.healthProgrammesLoading = false;
        // May be the API's { en, fr } pair; the component picks the language.
        state.healthProgrammesError = action.payload?.error || 'Failed to load this patient’s programmes';
      })

      .addCase(openClinicalDetail.pending, (state) => {
        state.clinicalLoading = true;
        state.clinicalError = null;
        state.clinicalErrorCode = null;
      })
      .addCase(openClinicalDetail.fulfilled, (state, action) => {
        state.clinicalLoading = false;
        state.clinical = action.payload || null;
      })
      .addCase(openClinicalDetail.rejected, (state, action) => {
        state.clinicalLoading = false;
        state.clinicalError = action.payload?.error || 'Failed to open the clinical detail';
        state.clinicalErrorCode = action.payload?.code || null;
      })

      .addCase(fetchServiceVisibility.pending, (state) => {
        state.servicesLoading = true;
        state.servicesError = null;
      })
      .addCase(fetchServiceVisibility.fulfilled, (state, action) => {
        state.servicesLoading = false;
        state.services = action.payload || [];
      })
      .addCase(fetchServiceVisibility.rejected, (state, action) => {
        state.servicesLoading = false;
        state.servicesError = action.payload?.error || 'Failed to load service visibility';
      })

      .addCase(saveServiceVisibility.pending, (state) => {
        state.servicesSaving = true;
      })
      .addCase(saveServiceVisibility.fulfilled, (state, action) => {
        state.servicesSaving = false;
        // The API answers with all six in catalogue order, so the response
        // replaces the list outright rather than being merged into it.
        state.services = action.payload || [];
      })
      .addCase(saveServiceVisibility.rejected, (state, action) => {
        state.servicesSaving = false;
        state.servicesError = action.payload?.error || 'Failed to save service visibility';
      });
  },
});

export const { resetMocPatientDetail, clearClinicalDetail } = mocSlice.actions;

export default mocSlice.reducer;
